import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Eye, Download, Trash2, Edit3, Lock, Server, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { HumaTwinIcon } from '../../components/brand/HumaTwinLogo';

// ============================================================
// PUBLIC PRIVACY PAGE
// Different from authenticated Privacy & Control dashboard
// ============================================================

export default function PublicPrivacy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white" data-testid="public-privacy">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <HumaTwinIcon size={28} />
              <span className="font-heading text-lg font-bold text-gray-900">HumaTwin</span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher variant="compact" />
              <Link to="/login">
                <Button variant="ghost" size="sm">Sign in</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-gray-900">
            Privacy & Data
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl">
            How HumaTwin handles your personal information
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Core Principles */}
          <section className="py-12">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8">
              Our Principles
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="p-6 bg-navy-50 rounded-2xl">
                <div className="w-12 h-12 rounded-xl bg-navy-100 flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-navy-700" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">You own your data</h3>
                <p className="text-sm text-gray-600">
                  Your personal evidence and representation belong to you. We process it to provide 
                  the service, not to build profiles for other purposes.
                </p>
              </div>
              <div className="p-6 bg-blue-50 rounded-2xl">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Full control</h3>
                <p className="text-sm text-gray-600">
                  You can inspect, correct, export and delete your information at any time 
                  through your account settings.
                </p>
              </div>
              <div className="p-6 bg-purple-50 rounded-2xl">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-purple-700" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Transparency</h3>
                <p className="text-sm text-gray-600">
                  HumaTwin is designed to show you the evidence behind what it understands 
                  about you, not hide how it works.
                </p>
              </div>
            </div>
          </section>

          {/* Information Categories */}
          <section className="py-12 border-t border-gray-100">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8">
              Information We Process
            </h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Account Information</h3>
                <p className="text-gray-600 mb-4">
                  Basic information needed to create and maintain your account.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Email address (for authentication and communication)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Name (as you choose to provide it)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Language preference
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Personal Evidence</h3>
                <p className="text-gray-600 mb-4">
                  Information you actively share to build your representation.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Identity information from sessions and questionnaires
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Reflections and thoughts you record
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Goals and aspirations you define
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Life events and milestones you share
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Documents & Knowledge Sources</h3>
                <p className="text-gray-600 mb-4">
                  Files and references you upload to inform your Twin.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Documents you upload (PDFs, text files)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    Notes and references
                  </li>
                </ul>
              </div>

              <div className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Conversation Information</h3>
                <p className="text-gray-600 mb-4">
                  Your interactions with your Twin.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    Messages you send to your Twin
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    Twin responses (generated based on your representation)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    Conversation history and context
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Why We Process */}
          <section className="py-12 border-t border-gray-100">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8">
              Why We Process Your Information
            </h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                HumaTwin processes your personal information to build and maintain your 
                digital representation. This includes:
              </p>
              <ul className="mt-4 space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-navy-700 text-xs font-bold">1</span>
                  </div>
                  <span>
                    <strong>Building your representation</strong> — Organizing evidence about your 
                    identity, knowledge, reflections, goals and life context.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-navy-700 text-xs font-bold">2</span>
                  </div>
                  <span>
                    <strong>Enabling your Twin</strong> — Allowing your representation to reason 
                    with you in conversations, grounded in the evidence you've provided.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-navy-700 text-xs font-bold">3</span>
                  </div>
                  <span>
                    <strong>Maintaining context</strong> — Preserving temporal and situational 
                    context so your representation can evolve accurately over time.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-navy-700 text-xs font-bold">4</span>
                  </div>
                  <span>
                    <strong>Service operation</strong> — Authentication, support, and ensuring 
                    the service works correctly.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Your Controls */}
          <section className="py-12 border-t border-gray-100">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8">
              Your Control
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Inspect</h3>
                  <p className="text-sm text-gray-600">
                    View all the evidence and information that forms your representation.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Edit3 className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Correct</h3>
                  <p className="text-sm text-gray-600">
                    Update or correct information if your Twin's understanding is inaccurate.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Download className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Export</h3>
                  <p className="text-sm text-gray-600">
                    Download a copy of your data and representation.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-5 h-5 text-red-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Delete</h3>
                  <p className="text-sm text-gray-600">
                    Remove specific evidence or your entire account permanently.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Important Notes */}
          <section className="py-12 border-t border-gray-100">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-8">
              Important Notes
            </h2>
            <div className="p-6 bg-amber-50 rounded-2xl border border-amber-200">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-2">Terminology</h3>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    Your personal information in HumaTwin is referred to as <strong>personal evidence</strong>, 
                    not "training data." HumaTwin organizes and retrieves your evidence to inform your 
                    representation — it does not use your personal information to train general AI models.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <div className="flex items-start gap-4">
                <Server className="w-6 h-6 text-gray-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">AI Processing</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    HumaTwin uses AI to generate responses based on your representation. This processing 
                    happens in real-time when you interact with your Twin. The AI is grounded in the 
                    evidence you have provided and is designed to acknowledge uncertainty when evidence 
                    is insufficient.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-12 border-t border-gray-100">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">
              Privacy Inquiries
            </h2>
            <p className="text-gray-600 mb-6">
              If you have questions about how HumaTwin handles your information, you can reach us through 
              your account settings when logged in, or contact our support team.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/login">
                <Button className="bg-navy-800 hover:bg-navy-900">
                  Sign in to manage your data
                </Button>
              </Link>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <HumaTwinIcon size={20} />
              <span className="text-sm text-gray-500">© {new Date().getFullYear()} HumaTwin</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/" className="hover:text-gray-900">Home</Link>
              <Link to="/terms" className="hover:text-gray-900">Terms</Link>
              <Link to="/login" className="hover:text-gray-900">Sign in</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
